import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { usePublicConfig } from '../utils/publicConfig';
import { useAuth } from '../context/AuthContext';

const ADMIN_ROLES = ['admin', 'super_admin', 'panel_admin'];
export const isPanelAdmin = (user) => !!(user && ADMIN_ROLES.includes(user.role));

// Logged-in admin on a public page: name + role, menu with Dashboard / Log out.
function AdminMenu({ user }) {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  const name = user.username || user.steam_id || '';
  const item = { display: 'block', width: '100%', textAlign: 'left', padding: '0.45rem 0.7rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', background: 'none', border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer', textDecoration: 'none', fontFamily: 'inherit' };
  const hover = { onMouseEnter: e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = 'var(--text-primary)'; }, onMouseLeave: e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--text-secondary)'; } };
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="menu" aria-expanded={open}
        title={t('publicHeader.loggedInAs', 'Logged in as {{name}}', { name })}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.45rem', padding: '0.22rem 0.6rem 0.22rem 0.25rem',
          background: open ? 'rgba(255,255,255,0.05)' : 'transparent', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)',
          color: 'var(--text-primary)', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
        }}>
        <span style={{ width: 22, height: 22, borderRadius: 5, background: 'rgba(59,130,246,0.15)', color: 'var(--accent-blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700 }}>
          {name.charAt(0).toUpperCase()}
        </span>
        <span style={{ fontSize: '0.8125rem', fontWeight: 600, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent-green)' }} />
      </button>
      {open && (
        <div role="menu" style={{
          position: 'absolute', right: 0, top: 'calc(100% + 6px)', minWidth: 190, zIndex: 200, padding: '0.35rem',
          background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', boxShadow: '0 10px 30px rgba(0,0,0,0.45)',
        }}>
          <div style={{ padding: '0.4rem 0.7rem 0.5rem', borderBottom: '1px solid var(--border-subtle)', marginBottom: '0.3rem' }}>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{t('publicHeader.loggedIn', 'Logged in')}</div>
            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
            {user.role && <div style={{ fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted)', marginTop: 2 }}>{user.role.replace(/_/g, ' ')}</div>}
          </div>
          <Link role="menuitem" to="/admin/dashboard" onClick={() => setOpen(false)} style={item} {...hover}>{t('nav.dashboard', 'Dashboard')}</Link>
          <button role="menuitem" type="button" onClick={() => { setOpen(false); logout(); navigate('/'); }} style={{ ...item, color: 'var(--accent-red)' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; }} onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
            {t('publicHeader.logout', 'Log out')}
          </button>
        </div>
      )}
    </div>
  );
}

function PublicHeader() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const loggedIn = isPanelAdmin(user);
  // Settings → About page / Beta notice switches (super admin). About shows by default; the Beta
  // notice waits for the flags so a hidden notice never flashes.
  const cfg = usePublicConfig();
  const hideAbout = !!(cfg && cfg.hide_about_page);
  const showBeta = !!(cfg && !cfg.hide_beta_banner);
  return (
    <>
    <header style={{
      background: 'var(--bg-card)',
      borderBottom: '1px solid var(--border-subtle)',
      padding: '0 0',
      position: 'sticky',
      top: 0,
      zIndex: 100,
      height: 52,
      display: 'flex',
      alignItems: 'center',
    }}>
      <div className="container flex justify-between items-center" style={{ width: '100%' }}>
        <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1rem', letterSpacing: '-0.02em' }}>
          <img src="/logo.png" alt="Flux" style={{ width: 24, height: 24, borderRadius: 6 }} />
          <span style={{ color: 'var(--text-primary)' }}>Flux</span>
        </Link>

        <div style={{ display: 'flex', gap: 'clamp(0.25rem, 1.5vw, 0.5rem)', alignItems: 'center' }}>
          {[
            { to: '/matches', label: t('nav.matches', 'Matches') },
            { to: '/teams', label: t('nav.teams', 'Teams') },
            ...(!hideAbout ? [{ to: '/about', label: t('nav.about', 'About') }] : []),
            ...(!loggedIn ? [{ to: '/admin', label: t('nav.login', 'Login') }] : []),
          ].map(link => (
            <Link key={link.to} to={link.to} style={{
              color: 'var(--text-secondary)', textDecoration: 'none', fontSize: '0.8125rem', fontWeight: 600,
              padding: '0.3rem clamp(0.45rem, 2vw, 0.75rem)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-default)',
              transition: 'color 0.15s, border-color 0.15s', whiteSpace: 'nowrap',
            }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.borderColor = 'var(--accent-blue)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.borderColor = 'var(--border-default)'; }}
            >
              {link.label}
            </Link>
          ))}
          {loggedIn && <AdminMenu user={user} />}
          <LanguageSwitcher compact />
        </div>
      </div>
    </header>
    {showBeta && (
    <div style={{
      background: 'var(--bg-card)',
      borderBottom: '1px solid var(--border-subtle)',
      padding: '0.4rem 1rem',
      textAlign: 'center',
      fontSize: '0.72rem',
      color: 'var(--text-muted)',
      lineHeight: 1.5,
    }}>
      <span style={{ color: 'var(--accent-amber)', fontWeight: 600, marginRight: 4, fontSize: '0.75rem' }}>{t('common.beta', 'Beta')}</span>
      {t('publicHeader.betaJoin', 'To report bugs or request access, join')}{' '}
      <a href="https://discord.gg/PdghFH7TU8" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-secondary)', fontWeight: 600, textDecoration: 'none', transition: 'color 0.15s' }}
         onMouseEnter={e => e.currentTarget.style.color = 'var(--text-primary)'}
         onMouseLeave={e => e.currentTarget.style.color = 'var(--text-secondary)'}
      >
        Discord
      </a>
    </div>
    )}
    </>
  );
}

export default PublicHeader;
